"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { m } from "framer-motion"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { PlaceholderMedia } from "@/components/ui/placeholder-media"
import { InsuranceCoverageCta } from "@/components/marketing/insurance-coverage-cta"
import {
  AddonBookingCta,
  type BiomarkerLandingConfig,
  type LandingCard,
} from "@/components/ui/biomarker-landing"

const EXPO = [0.16, 1, 0.3, 1] as const

/** Step media — real image (object-cover in a fixed 4:3 box) or a placeholder. */
function StepMedia({
  item,
  priority,
  sizes,
}: {
  item: LandingCard
  priority?: boolean
  sizes: string
}) {
  if (!item.image) {
    return <PlaceholderMedia label={item.title} className="h-full w-full" />
  }
  return (
    <Image
      src={item.image.src}
      alt={item.image.alt}
      fill
      priority={priority}
      sizes={sizes}
      className="object-cover"
      style={
        item.image.position
          ? { objectPosition: item.image.position }
          : undefined
      }
    />
  )
}

/**
 * "How it works" — scroll-linked step walkthrough for biomarker LPs. A faithful
 * config-driven copy of the full-body-MRI VisitWalkthroughTimeline: mobile is a
 * plain stacked list; desktop is a tall sticky section whose active step
 * advances as you scroll, with a connector rail that fills to the active node
 * and a detail panel (fixed 4:3 image + copy) that swaps per step. Clicking a
 * node scrolls to that step. Step 1 carries the booking + insurance CTAs; the
 * final step carries the "view sample report" CTA.
 */
export function BiomarkerStepsWalkthrough({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { steps } = config
  const items = steps.items
  const total = items.length
  const lp = useLocalePath()
  const [activeStep, setActiveStep] = useState(0)
  const sectionRef = useRef<HTMLDivElement>(null)

  // Scroll progress through the tall section drives the active step.
  useEffect(() => {
    const section = sectionRef.current
    if (!section) return
    let raf = 0
    const compute = () => {
      raf = 0
      const rect = section.getBoundingClientRect()
      const scrollRange = section.offsetHeight - window.innerHeight
      if (scrollRange <= 0) return
      const p = Math.max(0, Math.min(1, -rect.top / scrollRange))
      setActiveStep(Math.min(total - 1, Math.floor(p * total)))
    }
    const onScroll = () => {
      if (raf === 0) raf = requestAnimationFrame(compute)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    compute()
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [total])

  // Click a node → scroll the page to the middle of that step's zone.
  const jumpTo = (i: number) => {
    const section = sectionRef.current
    if (!section) return
    const scrollRange = section.offsetHeight - window.innerHeight
    const sectionTop = section.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top: sectionTop + ((i + 0.5) / total) * scrollRange,
      behavior: "smooth",
    })
  }

  const step = items[activeStep]
  // Discrete fill so the bar lands exactly on the active node (step 2 → 50%),
  // whether reached by scroll or by clicking a node.
  const fillPct = total > 1 ? (activeStep / (total - 1)) * 100 : 0
  const stepWord = steps.stepWord ?? "Step"

  // Per-step CTAs: step 1 books; the final step links to the example report.
  const renderCtas = (i: number) => {
    if (i === 0) {
      return (
        <div className="mt-8 flex flex-col flex-wrap items-start gap-4 sm:flex-row sm:items-center">
          <AddonBookingCta location={`${config.slug}_howitworks`}>
            {config.hero.primaryCta}
          </AddonBookingCta>
          <InsuranceCoverageCta
            size="md"
            icon="heart-check-pulse"
            className="hover:bg-transparent"
          />
        </div>
      )
    }
    if (i === total - 1 && steps.sampleReportCta) {
      return (
        <div className="mt-8">
          <Link
            href={lp("/report")}
            className="type-title-sm inline-flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-7 py-3.5 font-medium text-neutral-900 transition-colors hover:bg-neutral-100 sm:w-auto"
          >
            {steps.sampleReportCta}
          </Link>
        </div>
      )
    }
    // Middle step(s): link to the locations page.
    if (i > 0 && i < total - 1 && steps.locationsCta) {
      return (
        <div className="mt-8">
          <Link
            href={lp("/locations")}
            className="type-title-sm inline-flex w-full items-center justify-center gap-2 rounded-full border border-neutral-300 px-7 py-3.5 font-medium text-neutral-900 transition-colors hover:bg-neutral-100 sm:w-auto"
          >
            {steps.locationsCta}
          </Link>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-background mt-12 py-3 md:mt-24 md:py-4">
      <div className="mx-auto max-w-[1440px] px-4 md:px-10">
        {/* Mobile: stacked, no sticky */}
        <section className="rounded-[24px] bg-white py-14 md:hidden">
          <div className="mx-auto w-full px-5 text-center">
            <p className="type-label-lg mb-3 text-neutral-500 uppercase">
              {steps.eyebrow}
            </p>
            <h2 className="type-display-md text-neutral-900">
              {steps.heading}
            </h2>
          </div>
          <div className="mx-auto mt-10 flex w-full flex-col gap-12 px-5">
            {items.map((s, i) => (
              <div key={s.title}>
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-neutral-200">
                  <StepMedia item={s} priority={i === 0} sizes="100vw" />
                </div>
                <p className="type-label-sm bg-deep-green-50 text-deep-green-700 mt-4 inline-flex items-center rounded-full px-3 py-1 uppercase">
                  {stepWord} {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="type-title-lg mt-2 text-neutral-900">
                  {s.title}
                </h3>
                <p className="type-body-md mt-2 text-neutral-600">{s.body}</p>
                {renderCtas(i)}
              </div>
            ))}
          </div>
        </section>

        {/* Desktop: sticky scroll-linked clickable stepper */}
        <section
          ref={sectionRef}
          className="relative hidden rounded-[32px] bg-white md:block"
          style={{ minHeight: `${total * 100}vh` }}
        >
          <div className="sticky top-0 flex h-screen flex-col justify-center py-16">
            <div className="mx-auto mb-10 w-full max-w-[620px] px-4 text-center md:mb-12">
              <p className="type-label-lg mb-3 text-neutral-500 uppercase">
                {steps.eyebrow}
              </p>
              <h2 className="type-display-md text-neutral-900">
                {steps.heading}
              </h2>
            </div>

            <div className="mx-auto w-full max-w-[1240px] px-6 md:px-16">
              {/* Stepper rail — nodes on a line that fills to the active step */}
              <div className="relative mx-[10%]">
                <div className="absolute top-1/2 right-0 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-200" />
                <div
                  className="absolute top-1/2 left-0 h-0.5 -translate-y-1/2 rounded-full bg-neutral-900 transition-[width] duration-500 ease-out"
                  style={{ width: `${fillPct}%` }}
                />
                <div className="relative flex justify-between">
                  {items.map((item, i) => {
                    const done = i <= activeStep
                    return (
                      <button
                        key={item.title}
                        type="button"
                        aria-current={i === activeStep}
                        aria-label={item.title}
                        onClick={() => jumpTo(i)}
                        className={cn(
                          "flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-[13px] leading-none font-medium ring-4 ring-white transition-[transform,background-color,color] duration-300 outline-none focus:outline-none focus-visible:outline-none", // ds-typo-ignore
                          done
                            ? "bg-neutral-900 text-white"
                            : "bg-white text-neutral-900 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.18)]",
                          i === activeStep && "scale-110"
                        )}
                      >
                        {i + 1}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Detail panel — swaps with the active step */}
              <m.div
                key={activeStep}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.45,
                  ease: EXPO as unknown as number[],
                }}
                className="mt-12 grid items-center gap-10 md:grid-cols-2 md:gap-16"
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[16px] bg-neutral-200">
                  <StepMedia
                    item={step}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                  />
                </div>
                <div>
                  <p className="type-label-sm bg-deep-green-50 text-deep-green-700 inline-flex items-center rounded-full px-3 py-1 uppercase">
                    {stepWord} {String(activeStep + 1).padStart(2, "0")}
                  </p>
                  <h3 className="type-display-sm mt-3 text-neutral-900">
                    {step.title}
                  </h3>
                  <p className="type-body-lg mt-3 text-neutral-600">
                    {step.body}
                  </p>
                  {renderCtas(activeStep)}
                </div>
              </m.div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
